import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { employerAPI, reviewsAPI } from '../../services/api';
import ScheduleReviewModal from '../../components/employer/ScheduleReviewModal';
import { toast } from 'sonner';
import { 
  User, Mail, Briefcase, MapPin, ChevronLeft, Calendar, FileText,
  Loader2, Star, Clock, CheckCircle, Plus, Edit, Building2, Shield
} from 'lucide-react';

const StaffDetail = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [scheduledReviews, setScheduledReviews] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const fetchData = async () => {
    try {
      // Get employee profile from talent profile endpoint
      const profileRes = await employerAPI.getTalentProfile(employeeId);
      setEmployee(profileRes.data?.profile || null);
      
      // Get records for this employee
      const recordsRes = await employerAPI.getRecords();
      const employeeRecords = (recordsRes.data || []).filter(r => r.employee_id === employeeId);
      setRecords(employeeRecords);
      
      // Get scheduled reviews for this employee
      const reviewsRes = await reviewsAPI.getScheduledReviews();
      const employeeReviews = (reviewsRes.data || []).filter(r => r.employee_id === employeeId);
      setScheduledReviews(employeeReviews);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load staff details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'Draft' },
      pending_acknowledgement: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Pending' },
      acknowledged: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Verified' },
      archived: { color: 'bg-[#F5F8FF] text-[#5B6472] border-[#E8EDF5]', label: 'Archived' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!employee) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-[#0A0F1E] mb-2">Staff member not found</h2>
          <Button onClick={() => navigate('/employer/company')} variant="outline" className="border-[#E8EDF5] text-[#0A0F1E]">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Company
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-detail-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/employer/company')}
          className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-transparent -ml-2"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Company
        </Button>

        {/* Profile Header */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {employee.photo_url ? (
              <img 
                src={employee.photo_url} 
                alt={`${employee.first_name} ${employee.last_name}`}
                className="w-24 h-24 rounded-xl object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-[#F5F8FF] flex items-center justify-center">
                <User className="w-12 h-12 text-[#5B6472]" />
              </div>
            )}
            
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-[#0A0F1E]">
                {employee.first_name} {employee.last_name}
              </h1>
              {employee.job_title && (
                <p className="text-[#1B5AFF] font-medium mt-1">{employee.job_title}</p>
              )}
              
              <div className="flex flex-wrap items-center gap-4 mt-3 text-[#5B6472] text-sm">
                {employee.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {employee.email}
                  </span>
                )}
                {employee.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {employee.location}
                  </span>
                )}
              </div>
              
              {employee.vett_id && (
                <div className="flex items-center gap-2 mt-3">
                  <Shield className="w-4 h-4 text-[#1B5AFF]" />
                  <span className="text-[#1B5AFF] font-mono font-medium">{employee.vett_id}</span>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setShowScheduleModal(true)}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
                data-testid="schedule-review-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedule Review
              </Button>
              <Button
                onClick={() => navigate(`/employer/records/new?employee=${employeeId}`)}
                variant="outline"
                className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
                data-testid="create-record-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Record
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-[#0A0F1E]">{records.length}</p>
            <p className="text-[#5B6472] text-sm">Records</p>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-[#0A0F1E]">{records.filter(r => r.status === 'acknowledged').length}</p>
            <p className="text-[#5B6472] text-sm">Verified</p>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-[#0A0F1E]">{scheduledReviews.length}</p>
            <p className="text-[#5B6472] text-sm">Scheduled Reviews</p>
          </Card>
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-[#0A0F1E]">
              {records.filter(r => r.rating).length > 0 
                ? (records.filter(r => r.rating).reduce((sum, r) => sum + r.rating, 0) / records.filter(r => r.rating).length).toFixed(1)
                : '-'}
            </p>
            <p className="text-[#5B6472] text-sm">Avg Rating</p>
          </Card>
        </div>

        {/* Records Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0A0F1E] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1B5AFF]" />
              VETT Records
            </h2>
            <Button
              onClick={() => navigate(`/employer/records/new?employee=${employeeId}`)}
              variant="outline"
              size="sm"
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Record
            </Button>
          </div>
          
          {records.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 text-center">
              <FileText className="w-10 h-10 text-[#5B6472] mx-auto mb-3" />
              <p className="text-[#5B6472]">No records yet</p>
              <Button
                onClick={() => navigate(`/employer/records/new?employee=${employeeId}`)}
                className="mt-4 bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                Create First Record
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {records.map((record) => (
                <Card 
                  key={record.id}
                  className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 hover:border-[#1B5AFF]/30 transition-colors cursor-pointer"
                  onClick={() => navigate(`/employer/records/${record.id}/edit`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">{record.job_title}</h3>
                      <p className="text-[#5B6472] text-sm">
                        {formatDate(record.start_date)} - {record.end_date ? formatDate(record.end_date) : 'Present'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {record.rating && (
                        <span className="flex items-center gap-1 text-amber-400">
                          <Star className="w-4 h-4 fill-amber-400" />
                          {record.rating}
                        </span>
                      )}
                      {getStatusBadge(record.status)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Scheduled Reviews Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#0A0F1E] flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#1B5AFF]" />
              Scheduled Reviews
            </h2>
            <Button
              onClick={() => setShowScheduleModal(true)}
              variant="outline"
              size="sm"
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1" />
              Schedule
            </Button>
          </div>
          
          {scheduledReviews.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 text-center">
              <Calendar className="w-10 h-10 text-[#5B6472] mx-auto mb-3" />
              <p className="text-[#5B6472]">No reviews scheduled</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {scheduledReviews.map((review) => (
                <Card 
                  key={review.id}
                  className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-[#0A0F1E] font-medium">{review.review_type}</h3>
                      <p className="text-[#5B6472] text-sm">Due: {formatDate(review.due_date)}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                      review.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                        : review.status === 'overdue'
                        ? 'bg-red-500/20 text-red-400 border-red-500/30'
                        : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                    }`}>
                      {review.status}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Schedule Review Modal */}
      <ScheduleReviewModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSuccess={() => {
          setShowScheduleModal(false);
          fetchData();
          toast.success('Review scheduled');
        }}
        prefilledEmployee={{
          id: employeeId,
          first_name: employee.first_name,
          last_name: employee.last_name,
          photo_url: employee.photo_url,
          job_title: employee.job_title
        }}
      />
    </DashboardLayout>
  );
};

export default StaffDetail;
